import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useStore from '../store/useStore';
import { 
  FileText, Download, ChevronRight, File, Loader2, Search,
  Filter, FolderOpen
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const DownloadsPage = () => {
  const { language } = useStore();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const t = {
    fr: {
      title: 'Documents à télécharger',
      subtitle: 'Retrouvez tous nos documents utiles',
      search: 'Rechercher...',
      allCategories: 'Toutes catégories',
      noDocuments: 'Aucun document disponible',
      download: 'Télécharger',
      home: 'Accueil',
      categories: {
        general: 'Général',
        legal: 'Documents légaux',
        guides: 'Guides',
        catalog: 'Catalogues'
      }
    },
    en: {
      title: 'Downloads',
      subtitle: 'Find all our useful documents',
      search: 'Search...',
      allCategories: 'All categories',
      noDocuments: 'No documents available',
      download: 'Download',
      home: 'Home',
      categories: {
        general: 'General',
        legal: 'Legal documents',
        guides: 'Guides',
        catalog: 'Catalogs'
      }
    },
    nl: {
      title: 'Downloads',
      subtitle: 'Vind al onze nuttige documenten',
      search: 'Zoeken...',
      allCategories: 'Alle categorieën',
      noDocuments: 'Geen documenten beschikbaar',
      download: 'Downloaden',
      home: 'Home',
      categories: {
        general: 'Algemeen',
        legal: 'Juridische documenten',
        guides: 'Gidsen',
        catalog: 'Catalogi'
      }
    }
  };

  const texts = t[language] || t.fr;

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/documents/public`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) {
      return (
        <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
          <span className="text-red-600 font-bold text-xs">PDF</span>
        </div>
      );
    }
    return (
      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
        <File className="w-6 h-6 text-blue-600" />
      </div>
    );
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Get unique categories
  const categories = [...new Set(documents.map(d => d.category))];

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      (doc[`title_${language}`] || doc.title_fr || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc[`description_${language}`] || doc.description_fr || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Group by category
  const groupedDocuments = filteredDocuments.reduce((acc, doc) => {
    const cat = doc.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <Link to="/shop" className="hover:text-amber-600">{texts.home}</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900">{texts.title}</span>
        </div>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
              <FileText className="w-7 h-7 text-amber-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{texts.title}</h1>
              <p className="text-gray-500">{texts.subtitle}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={texts.search}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="pl-10 pr-8 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 appearance-none bg-white"
              >
                <option value="all">{texts.allCategories}</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {texts.categories[cat] || cat}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Documents */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{texts.noDocuments}</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedDocuments).map(([category, docs]) => (
              <div key={category}>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  {texts.categories[category] || category}
                </h2>
                <div className="grid gap-4">
                  {docs.map((doc) => (
                    <div 
                      key={doc.id} 
                      className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
                    >
                      {getFileIcon(doc.file_type)}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">
                          {doc[`title_${language}`] || doc.title_fr || doc.filename}
                        </h3>
                        {(doc[`description_${language}`] || doc.description_fr) && (
                          <p className="text-sm text-gray-500 line-clamp-2">
                            {doc[`description_${language}`] || doc.description_fr}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {formatFileSize(doc.file_size)}
                        </p>
                      </div>
                      <a
                        href={doc.file_url}
                        download
                        className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors whitespace-nowrap"
                      >
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">{texts.download}</span>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadsPage;
